import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import {
    getDefaultStepId,
    getStepIdFromHash,
    getPersistedStep,
    persistStep,
    getStoredFromStep,
    setStoredFromStep,
    getWorkflowSteps,
} from "../Utils/literatureReviewWorkflow"
import { getLiteratureReview, initiatePaperSourceSearch, upgradeToPremium } from "../Utils/functions"
import { NavigationContext } from "./NavigationContext"
import useAccessCheck from "../hooks/useAccessCheck"

export const LiteratureReviewContext = createContext(null)

// Steps where the "fromStep === recommended-abstracts" filter applies
// (removes full-text-screening and recommended-sources from the sidebar timeline)
const FILTERABLE_STEPS = new Set([
    "data-extraction-definition",
    "data-extraction-results",
    "research-report",
])

const EMPTY_WORKFLOW_DATA = {
    question: "",
    screenedArticles: [],
    articleUniqueIds: [],
    articleValues: {},
    suggestedColumns: [],
    customColumns: [],
    fullTextArticleIds: null,
    fullTextUploadedCount: null,
    extractionColumns: [],
    extractionArticleValues: {},
    columnsList: [],
    litreviewtitle: "",
}

export const LiteratureReviewProvider = ({ children }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()
    const { setRefreshTrigger } = useContext(NavigationContext)

    // --- Step resolution (priority: route state → URL hash → localStorage → default) ---
    const hashFromUrl = (typeof window !== "undefined" && window.location.hash) || location.hash || ""
    const step =
        location.state?.step ??
        getStepIdFromHash(hashFromUrl) ??
        (id ? getPersistedStep(id) : null) ??
        getDefaultStepId()

    // --- fromStep: which step the user came from (affects sidebar filtering) ---
    const [fromStep, setFromStepState] = useState(
        () => (id ? getStoredFromStep(id) : null) ?? null
    )

    // --- Workflow data: in-memory store shared across all step pages ---
    const [workflowData, setWorkflowDataState] = useState(() => ({
        ...EMPTY_WORKFLOW_DATA,
        litreviewtitle: location.state?.litreviewtitle || "",
    }))

    // Reset per-review state when the review id changes
    const prevIdRef = useRef(id)
    useEffect(() => {
        if (prevIdRef.current !== id) {
            const previousId = prevIdRef.current
            prevIdRef.current = id
            const titleFromNav = location.state?.litreviewtitle || ""
            setWorkflowDataState((prev) => {
                // Keep an in-session title when moving from /screening → /screening/:id after create
                if (!previousId && id && (prev.litreviewtitle || prev.question)) {
                    return {
                        ...EMPTY_WORKFLOW_DATA,
                        litreviewtitle: prev.litreviewtitle || titleFromNav,
                        question: prev.question,
                    }
                }
                return {
                    ...EMPTY_WORKFLOW_DATA,
                    litreviewtitle: titleFromNav,
                }
            })
            setFromStepState(id ? getStoredFromStep(id) : null)
        }
    }, [id, location.state?.litreviewtitle])

    // Hydrate workflow from the job so deep links (e.g. Reports → report page → Back)
    // still have screening/extraction data in context.
    useEffect(() => {
        if (!id) return
        let cancelled = false
        const loadPersistedJob = async () => {
            try {
                const job = await getLiteratureReview(id)
                if (cancelled || !job) return
                const title = job.litreviewtitle || job.question || ""
                const extractionValues =
                    job.extractionArticleValues && typeof job.extractionArticleValues === "object"
                        ? job.extractionArticleValues
                        : null
                const allIds = Array.isArray(job.articleUniqueIds)
                    ? job.articleUniqueIds.map(String)
                    : []
                let uniqueIds = allIds
                if (extractionValues && Object.keys(extractionValues).length) {
                    const inExtraction = allIds.filter((articleId) =>
                        extractionValues[articleId] != null || extractionValues[String(articleId)] != null
                    )
                    uniqueIds = inExtraction.length ? inExtraction : Object.keys(extractionValues)
                }
                const sourceArticles = Array.isArray(job.addedArticles) && job.addedArticles.length
                    ? job.addedArticles
                    : (Array.isArray(job.screenedArticles) ? job.screenedArticles : [])
                const articlesById = new Map()
                sourceArticles.forEach((article) => {
                    const articleId = String(article?.uniqueId ?? article?.id ?? "")
                    if (articleId) articlesById.set(articleId, article)
                })
                const screenedArticles = uniqueIds
                    .map((articleId) => articlesById.get(String(articleId)))
                    .filter(Boolean)

                setWorkflowDataState((prev) => ({
                    ...prev,
                    litreviewtitle: title || prev.litreviewtitle,
                    question: job.question || prev.question,
                    screenedArticles: screenedArticles.length ? screenedArticles : prev.screenedArticles,
                    articleUniqueIds: uniqueIds.length ? uniqueIds : prev.articleUniqueIds,
                    articleValues:
                        job.articleValues && typeof job.articleValues === "object"
                            ? job.articleValues
                            : prev.articleValues,
                    suggestedColumns:
                        Array.isArray(job.suggestedColumns) && job.suggestedColumns.length
                            ? job.suggestedColumns
                            : prev.suggestedColumns,
                    extractionColumns:
                        Array.isArray(job.extractionColumns) && job.extractionColumns.length
                            ? job.extractionColumns
                            : prev.extractionColumns,
                    extractionArticleValues:
                        extractionValues && Object.keys(extractionValues).length
                            ? extractionValues
                            : prev.extractionArticleValues,
                    fullTextArticleIds:
                        Array.isArray(job.extractionFullTextIds) && job.extractionFullTextIds.length
                            ? job.extractionFullTextIds
                            : prev.fullTextArticleIds,
                }))
            } catch (error) {
                console.error("Failed to load persisted literature review:", error)
            }
        }
        loadPersistedJob()
        return () => {
            cancelled = true
        }
    }, [id])

    // Persist current step to localStorage + sync URL hash
    useEffect(() => {
        if (!id || !step) return
        persistStep(id, step)
        const currentHash = hashFromUrl.replace(/^#/, "").trim()
        if (currentHash !== step) {
            navigate(`/screening/${id}#${step}`, { replace: true })
        }
    }, [id, step, hashFromUrl, navigate])

    // --- Access state (subscription + enterprise) ---
    const { user, isSubscribed, isAdvanced, currentPlan, isEnterpriseActive, hasEnterprise, loading, errorMessage, setErrorMessage } =
        useAccessCheck()

    // --- Workflow data updater (merges partial updates) ---
    const setWorkflowData = useCallback((partial) => {
        setWorkflowDataState((prev) => ({ ...prev, ...partial }))
    }, [])

    // --- Navigation helpers ---
    const navigateToStep = useCallback(
        (stepId) => {
            if (id) {
                persistStep(id, stepId)
                navigate(`/screening/${id}#${stepId}`)
            }
        },
        [id, navigate]
    )

    const setFromStep = useCallback(
        (stepId) => {
            if (id) setStoredFromStep(id, stepId)
            setFromStepState(stepId)
        },
        [id]
    )

    // --- Computed steps helper (replaces per-page useMemo + filter duplication) ---
    const getStepsForPage = useCallback(
        (currentStepId) => {
            const baseSteps = getWorkflowSteps(currentStepId)
            if (FILTERABLE_STEPS.has(currentStepId) && fromStep === "recommended-abstracts") {
                return baseSteps.filter(
                    (s) => s.id !== "full-text-screening" && s.id !== "recommended-sources"
                )
            }
            return baseSteps
        },
        [fromStep]
    )

    // --- Top-level actions ---
    const handleNavigateToPaperSources = useCallback(
        async (question) => {
            try {
                setErrorMessage("")
                setWorkflowData({ question })
                const result = await initiatePaperSourceSearch(question)
                if (result.id) {
                    const defaultStep = getDefaultStepId()
                    persistStep(result.id, defaultStep)
                    setWorkflowData({
                        question,
                        litreviewtitle: result.litreviewtitle || question,
                    })
                    setRefreshTrigger((prev) => !prev)
                    navigate(`/screening/${result.id}#${defaultStep}`)
                }
            } catch (error) {
                console.error("Error during search:", error)
                setErrorMessage(error.message || "An unexpected error occurred. Please try again.")
            }
        },
        [navigate, setErrorMessage, setWorkflowData, setRefreshTrigger]
    )

    const handleSubscription = useCallback(async (planType = "advanced") => {
        await upgradeToPremium(planType)
    }, [])

    const closeErrorModal = useCallback(() => {
        setErrorMessage(null)
    }, [setErrorMessage])

    const value = useMemo(
        () => ({
            id,
            step,
            fromStep,
            user,
            isSubscribed,
            isAdvanced,
            currentPlan,
            isEnterpriseActive,
            hasEnterprise,
            loading,
            errorMessage,
            setErrorMessage,
            workflowData,
            setWorkflowData,
            navigateToStep,
            setFromStep,
            getStepsForPage,
            handleNavigateToPaperSources,
            handleSubscription,
            closeErrorModal,
        }),
        [
            id,
            step,
            fromStep,
            user,
            isSubscribed,
            isAdvanced,
            currentPlan,
            isEnterpriseActive,
            hasEnterprise,
            loading,
            errorMessage,
            setErrorMessage,
            workflowData,
            setWorkflowData,
            navigateToStep,
            setFromStep,
            getStepsForPage,
            handleNavigateToPaperSources,
            handleSubscription,
            closeErrorModal,
        ]
    )

    return (
        <LiteratureReviewContext.Provider value={value}>
            {children}
        </LiteratureReviewContext.Provider>
    )
}

export const useLiteratureReview = () => {
    const context = useContext(LiteratureReviewContext)
    if (!context) {
        throw new Error("useLiteratureReview must be used within a LiteratureReviewProvider")
    }
    return context
}
